<script setup lang="ts">
import { actionOutputPorts } from '../../../../../shared/flow-core/src/execution'
import { smartBezier } from '../../../../../shared/flow-core/src/geometry'
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import type { FlowGraphEdgeV1, FlowGraphNodeV1, FlowNodeType } from '../flowchart/flowGraph'

const props = defineProps<{
  nodes: FlowGraphNodeV1[]
  edges: FlowGraphEdgeV1[]
  activeNodeId: string | null
}>()

const NODE_WIDTH = 260
const NODE_HEIGHT = 130
const WORLD_SIZE = 40000
const WORLD_ORIGIN = WORLD_SIZE / 2
const CONDITIONAL_TOP_RATIO = 0.36
const CONDITIONAL_BOTTOM_RATIO = 0.64

const canvasEl = ref<HTMLElement | null>(null)
const viewport = ref({ x: 0, y: 0, scale: 1 })

let panActive = false
let panStart = { mouseX: 0, mouseY: 0, vx: 0, vy: 0 }

const nodeX = (n: FlowGraphNodeV1) => n.position?.x ?? 0
const nodeY = (n: FlowGraphNodeV1) => n.position?.y ?? 0

const nodeById = computed(() => new Map(props.nodes.map((n) => [n.id, n])))

const nPorts = (count: number, prefix: string): string[] =>
  Array.from({ length: Math.max(1, count) }, (_, i) => `${prefix}${i}`)

const outputPorts = (node: FlowGraphNodeV1): string[] => {
  const d = node.data ?? {}
  if (node.type === 'condition') return ['true', 'else']
  if (node.type === 'end') return []
  if (node.type === 'macro-start') return nPorts(typeof d.inputCount === 'number' ? d.inputCount : 1, 'out-')
  if (node.type === 'macro-end') return nPorts(typeof d.outputCount === 'number' ? d.outputCount : 1, 'out-')
  if (node.type === 'macro-call') return nPorts(typeof d.outputCount === 'number' ? d.outputCount : 1, 'out-')
  if (node.type === 'action') return actionOutputPorts(d) ?? ['next']
  return ['next']
}

const inputPorts = (node: FlowGraphNodeV1): string[] => {
  const d = node.data ?? {}
  if (node.type === 'start') return []
  if (node.type === 'condition' || node.type === 'end') return ['in-a', 'in-b']
  if (node.type === 'macro-start') return nPorts(typeof d.inputCount === 'number' ? d.inputCount : 1, 'in-')
  if (node.type === 'macro-end') return nPorts(typeof d.outputCount === 'number' ? d.outputCount : 1, 'in-')
  if (node.type === 'macro-call') return nPorts(typeof d.inputCount === 'number' ? d.inputCount : 1, 'in-')
  return ['in']
}

const nodeTypeLabel = (type: FlowNodeType) => {
  if (type === 'start') return 'Session Start'
  if (type === 'phase') return 'Phase Block'
  if (type === 'group') return 'Group Gate'
  if (type === 'condition') return 'Condition'
  if (type === 'action') return 'Action'
  if (type === 'macro-start') return 'Macro Start'
  if (type === 'macro-end') return 'Macro End'
  if (type === 'macro-call') return 'Macro'
  return 'Flow End'
}

const nodeSummary = (node: FlowGraphNodeV1): string => {
  const d = node.data ?? {}
  if (node.type === 'condition') {
    const l = d.condLeft ?? 'R1'
    const op = d.condOp ?? '=='
    const r = d.condRight ?? 'literal'
    const lStr = l === 'literal' ? String(d.condLeftLit ?? 0) : String(l)
    const rStr = r === 'literal' ? String(d.condRightLit ?? 0) : String(r)
    return `${lStr} ${op} ${rStr}`
  }
  if (node.type === 'phase') return d.phaseId ? `Phase: ${d.phaseId}` : 'No phase ID'
  if (node.type === 'group') {
    if (d.groupId === '__all__') return 'All cards (deck)'
    return d.groupId ? String(d.groupId) : 'No group selected'
  }
  if (node.type === 'action') {
    const t = d.actionType || ''
    if (t === 'get-value') return `${d.getRegister ?? 'R1'} ← ${d.getSource ?? '?'}`
    if (t === 'set-value') return `${d.setTarget ?? '?'} ← ${d.setInput ?? 'R1'}`
    if (t === 'math-op') {
      const op = d.mathOp ?? '+1'
      const simple = op === '+1' || op === '-1'
      const operand = simple ? '' : ` ${d.mathOperand === 'literal' ? String(d.mathLit ?? 0) : (d.mathOperand ?? 'R1')}`
      return `${d.mathTarget ?? 'R1'} ${op}${operand}`
    }
    return String(t) || 'No action'
  }
  if (node.type === 'start') return 'Entry point'
  if (node.type === 'macro-start') return d.macroName ? `"${d.macroName}" · ${d.inputCount ?? 1} in` : '(unnamed)'
  if (node.type === 'macro-end') return `→ "${d.macroRef || '?'}" · ${d.outputCount ?? 1} out`
  if (node.type === 'macro-call') return d.macroRef ? `Call "${d.macroRef}"` : '(no macro selected)'
  return 'Terminal'
}

const inputPoint = (node: FlowGraphNodeV1, port: string) => {
  const ports = inputPorts(node)
  const index = Math.max(0, ports.indexOf(port))
  const ratio = ports.length > 1 ? (index === 0 ? 0.38 : 0.62) : 0.5
  return { x: nodeX(node) + WORLD_ORIGIN, y: nodeY(node) + WORLD_ORIGIN + NODE_HEIGHT * ratio }
}

const outputPoint = (node: FlowGraphNodeV1, port: string) => {
  if (node.type === 'condition') {
    const ratio = port === 'true' ? CONDITIONAL_TOP_RATIO : CONDITIONAL_BOTTOM_RATIO
    return { x: nodeX(node) + WORLD_ORIGIN + NODE_WIDTH, y: nodeY(node) + WORLD_ORIGIN + NODE_HEIGHT * ratio }
  }
  const ports = outputPorts(node)
  const index = Math.max(0, ports.indexOf(port))
  return {
    x: nodeX(node) + WORLD_ORIGIN + NODE_WIDTH,
    y: nodeY(node) + WORLD_ORIGIN + (NODE_HEIGHT * (index + 1)) / (ports.length + 1),
  }
}



const edgeViews = computed(() =>
  props.edges
    .map((edge) => {
      const from = nodeById.value.get(edge.fromNodeId)
      const to = nodeById.value.get(edge.toNodeId)
      if (!from || !to) return null
      return { id: edge.id, d: smartBezier(outputPoint(from, edge.fromPort), inputPoint(to, edge.toPort)) }
    })
    .filter(Boolean) as Array<{ id: string; d: string }>,
)

const gridStyle = computed(() => {
  const size = Math.max(6, 28 * viewport.value.scale)
  const offsetX = ((viewport.value.x % size) + size) % size
  const offsetY = ((viewport.value.y % size) + size) % size
  return { backgroundSize: `${size}px ${size}px`, backgroundPosition: `${offsetX}px ${offsetY}px` }
})

// Zoom

const zoomAt = (factor: number, clientX?: number, clientY?: number) => {
  const el = canvasEl.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const cx = typeof clientX === 'number' ? clientX : rect.left + rect.width / 2
  const cy = typeof clientY === 'number' ? clientY : rect.top + rect.height / 2
  const oldScale = viewport.value.scale
  const newScale = Math.max(0.1, Math.min(4, oldScale * factor))
  if (newScale === oldScale) return
  const lx = cx - rect.left, ly = cy - rect.top
  const wx = (lx - viewport.value.x) / oldScale, wy = (ly - viewport.value.y) / oldScale
  viewport.value = { scale: newScale, x: lx - wx * newScale, y: ly - wy * newScale }
}

const onWheel = (e: WheelEvent) => {
  e.preventDefault()
  zoomAt(e.deltaY < 0 ? 1.08 : 1 / 1.08, e.clientX, e.clientY)
}

// Pan

const onPanMove = (e: MouseEvent) => {
  if (!panActive) return
  viewport.value = {
    ...viewport.value,
    x: panStart.vx + (e.clientX - panStart.mouseX),
    y: panStart.vy + (e.clientY - panStart.mouseY),
  }
}

const onPanUp = () => {
  panActive = false
  window.removeEventListener('mousemove', onPanMove)
  window.removeEventListener('mouseup', onPanUp)
}

const onMouseDown = (e: MouseEvent) => {
  if (e.button === 0 || e.button === 1) {
    e.preventDefault()
    panActive = true
    panStart = { mouseX: e.clientX, mouseY: e.clientY, vx: viewport.value.x, vy: viewport.value.y }
    window.addEventListener('mousemove', onPanMove)
    window.addEventListener('mouseup', onPanUp)
  }
}

// Auto-fit / centering

/** Fits the whole graph in view. On graphs with many spread-out nodes this can
 * shrink everything down to near-unreadable — kept around for callers that
 * explicitly want a full overview, but it is NOT the default view anymore. */
const fitToContent = () => {
  const el = canvasEl.value
  if (!el || props.nodes.length === 0) return
  const xs = props.nodes.map(nodeX)
  const ys = props.nodes.map(nodeY)
  const minX = Math.min(...xs), minY = Math.min(...ys)
  const maxX = Math.max(...xs) + NODE_WIDTH, maxY = Math.max(...ys) + NODE_HEIGHT
  const pad = 60
  const scale = Math.min(el.clientWidth / (maxX - minX + pad * 2), el.clientHeight / (maxY - minY + pad * 2), 1.5)
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2
  viewport.value = {
    scale,
    x: el.clientWidth / 2 - (WORLD_ORIGIN + cx) * scale,
    y: el.clientHeight / 2 - (WORLD_ORIGIN + cy) * scale,
  }
}

const centerOnNode = (node: FlowGraphNodeV1 | undefined, scale: number) => {
  const el = canvasEl.value
  if (!el || !node) return
  const cx = nodeX(node) + NODE_WIDTH / 2
  const cy = nodeY(node) + NODE_HEIGHT / 2
  viewport.value = {
    scale,
    x: el.clientWidth / 2 - (WORLD_ORIGIN + cx) * scale,
    y: el.clientHeight / 2 - (WORLD_ORIGIN + cy) * scale,
  }
}

/** Every node directly connected to `nodeId` by an edge, in either direction,
 * plus the node itself. */
const neighborhoodOf = (nodeId: string): Set<string> => {
  const ids = new Set<string>([nodeId])
  for (const edge of props.edges) {
    if (edge.fromNodeId === nodeId) ids.add(edge.toNodeId)
    if (edge.toNodeId === nodeId) ids.add(edge.fromNodeId)
  }
  return ids
}

/**
 * Centers on the currently-executing node (falling back to the first node),
 * framing it together with its directly-connected neighbors so the edges
 * to/from it are actually visible — this is what the panel opens on.
 *
 * A plain "center on just this node at a fixed zoom" looked right in
 * isolation, but on graphs where connected nodes sit further apart than the
 * panel is wide, the neighbor at the other end of an edge (and therefore the
 * edge itself) fell completely outside the viewport — so nothing was there
 * to draw a connecting line to. Fitting the node's local neighborhood
 * instead guarantees at least its immediate connections are on screen.
 */
const centerOnActiveNode = () => {
  const el = canvasEl.value
  if (!el) return

  const active = props.activeNodeId ? nodeById.value.get(props.activeNodeId) : undefined
  const target = active ?? props.nodes[0]
  if (!target) return

  const focusIds = neighborhoodOf(target.id)
  const focusNodes = props.nodes.filter((n) => focusIds.has(n.id))
  if (focusNodes.length <= 1) {
    // No edges touch this node (e.g. an isolated node, or none loaded yet) —
    // just center on it at a comfortably readable zoom.
    centerOnNode(target, 1)
    return
  }

  const xs = focusNodes.map(nodeX)
  const ys = focusNodes.map(nodeY)
  const minX = Math.min(...xs), minY = Math.min(...ys)
  const maxX = Math.max(...xs) + NODE_WIDTH, maxY = Math.max(...ys) + NODE_HEIGHT
  const pad = 90
  const scale = Math.max(
    0.45,
    Math.min(el.clientWidth / (maxX - minX + pad * 2), el.clientHeight / (maxY - minY + pad * 2), 1.1),
  )
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2
  viewport.value = {
    scale,
    x: el.clientWidth / 2 - (WORLD_ORIGIN + cx) * scale,
    y: el.clientHeight / 2 - (WORLD_ORIGIN + cy) * scale,
  }
}

// The panel this canvas lives in is teleported/resized right around mount
// time (drag/resize panel, Teleport re-parenting), so `canvasEl.clientWidth`
// can still be 0 on the first frame or two — centering against that lands
// the viewport in the wrong place. Retry across a few frames until the
// container actually has real layout.
const centerWhenReady = (attempt = 0) => {
  requestAnimationFrame(() => {
    const el = canvasEl.value
    if (el && el.clientWidth > 0 && el.clientHeight > 0) {
      centerOnActiveNode()
      return
    }
    if (attempt < 20) centerWhenReady(attempt + 1)
  })
}

onMounted(() => { centerWhenReady() })
onBeforeUnmount(() => { onPanUp() })

// Re-center whenever the node set actually changes (e.g. graph swapped while
// the panel stays open) so the view doesn't stay centered on stale content.
watch(
  () => props.nodes,
  () => centerWhenReady(),
)

defineExpose({ fitToContent, centerOnActiveNode })
</script>

<template>
  <div
    ref="canvasEl"
    class="pfc-ro-canvas"
    @mousedown="onMouseDown"
    @wheel.prevent="onWheel"
  >
    <div class="pfc-ro-grid" :style="gridStyle" aria-hidden="true" />
    <div
      class="pfc-ro-world"
      :style="{
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
        transformOrigin: '0 0',
      }"
    >
      <svg class="pfc-ro-edges" :width="WORLD_SIZE" :height="WORLD_SIZE">
        <path
          v-for="edge in edgeViews"
          :key="edge.id"
          :d="edge.d"
          class="pfc-ro-edge"
        />
      </svg>

      <article
        v-for="node in nodes"
        :key="node.id"
        class="pfc-ro-node"
        :class="[
          `pfc-ro-node--${node.type}`,
          { 'pfc-ro-node--active': node.id === activeNodeId },
        ]"
        :style="{ left: `${nodeX(node) + WORLD_ORIGIN}px`, top: `${nodeY(node) + WORLD_ORIGIN}px` }"
      >
        <div
          v-for="(port, index) in inputPorts(node)"
          :key="`in-${port}`"
          class="pfc-ro-handle pfc-ro-handle--input"
          :class="{
            'pfc-ro-handle--input-top': inputPorts(node).length === 2 && index === 0,
            'pfc-ro-handle--input-bottom': inputPorts(node).length === 2 && index === 1,
            [`pfc-ro-handle--port-${index}`]: node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call',
          }"
          :style="inputPorts(node).length > 2 ? { top: `${(100 / (inputPorts(node).length + 1)) * (index + 1)}%` } : {}"
        />

        <div
          v-for="(port, index) in outputPorts(node)"
          :key="`out-${port}`"
          class="pfc-ro-handle pfc-ro-handle--output"
          :class="{
            'pfc-ro-handle--output-top': node.type === 'condition' && port === 'true',
            'pfc-ro-handle--output-bottom': node.type === 'condition' && port === 'else',
            [`pfc-ro-handle--port-${index}`]: node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call',
          }"
          :style="outputPorts(node).length > 1 && node.type !== 'condition' ? { top: `${(100 / (outputPorts(node).length + 1)) * (index + 1)}%` } : {}"
        >
          {{ (node.type === 'macro-start' || node.type === 'macro-end' || node.type === 'macro-call') ? index : (port === 'next' ? '' : port) }}
        </div>

        <header class="pfc-ro-node__header">
          <strong>{{ node.label }}</strong>
          <span>{{ nodeTypeLabel(node.type) }}</span>
        </header>
        <div class="pfc-ro-node__body">
          <p class="pfc-ro-node__summary">{{ nodeSummary(node) }}</p>
        </div>
      </article>
    </div>
  </div>
</template>

<style scoped>
.pfc-ro-canvas {
  position: relative;
  width: 100%;
  height: 100%;
  background: #f8fafc;
  overflow: hidden;
  cursor: grab;
  touch-action: none;
  user-select: none;
}
.pfc-ro-canvas:active { cursor: grabbing; }

.pfc-ro-grid {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: radial-gradient(circle, #cbd5e1 1px, transparent 1px);
}

.pfc-ro-world {
  position: relative;
  width: 40000px;
  height: 40000px;
  z-index: 1;
  user-select: none;
}

.pfc-ro-edges {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.pfc-ro-edge {
  fill: none;
  stroke: #334155;
  stroke-width: 2.3;
  stroke-linecap: round;
  pointer-events: none;
}

/* Nodes */
.pfc-ro-node {
  position: absolute;
  z-index: 1;
  width: 260px;
  min-height: 130px;
  border: 1px solid #cbd5e1;
  border-radius: 0.65rem;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
  user-select: none;
  cursor: default;
  pointer-events: none;
}

.pfc-ro-node--active {
  outline: 3px solid #22c55e;
  outline-offset: 3px;
  box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.18);
  z-index: 2;
}

.pfc-ro-node__header {
  height: 36px;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 0.65rem 0.65rem 0 0;
  padding: 0.35rem 0.55rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  pointer-events: none;
  gap: 0.3rem;
}

.pfc-ro-node__header strong {
  font-size: 0.82rem;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pfc-ro-node__header span {
  font-size: 0.68rem;
  color: #94a3b8;
  white-space: nowrap;
  flex-shrink: 0;
}

.pfc-ro-node__body {
  padding: 0.55rem;
  min-height: 90px;
  pointer-events: none;
}

.pfc-ro-node__summary {
  margin: 0;
  font-size: 0.84rem;
  color: #334155;
}

/* Handles (visual only) */
.pfc-ro-handle {
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #fca5a5;
  background: #fee2e2;
  font-size: 8px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 4;
}

.pfc-ro-handle--input       { left: -9px; top: calc(50% - 9px); }
.pfc-ro-handle--input-top   { top: calc(38% - 9px); }
.pfc-ro-handle--input-bottom { top: calc(62% - 9px); }
.pfc-ro-handle--output       { right: -9px; top: calc(50% - 9px); }
.pfc-ro-handle--output-top   { top: calc(36% - 9px); }
.pfc-ro-handle--output-bottom { top: calc(64% - 9px); }

/* Node type colours */
.pfc-ro-node--start  { border-color: #22c55e; }
.pfc-ro-node--start  .pfc-ro-node__header { background: #ecfdf3; border-bottom-color: #bbf7d0; }
.pfc-ro-node--end    { border-color: #ef4444; }
.pfc-ro-node--end    .pfc-ro-node__header { background: #fef2f2; border-bottom-color: #fecaca; }
.pfc-ro-node--phase  { border-color: #f59e0b; }
.pfc-ro-node--phase  .pfc-ro-node__header { background: #fffbeb; border-bottom-color: #fde68a; }
.pfc-ro-node--group  { border-color: #0ea5e9; }
.pfc-ro-node--group  .pfc-ro-node__header { background: #f0f9ff; border-bottom-color: #bae6fd; }
.pfc-ro-node--condition { border-color: #f97316; }
.pfc-ro-node--condition .pfc-ro-node__header { background: #fff7ed; border-bottom-color: #fed7aa; }
.pfc-ro-node--action    { border-color: #6366f1; }
.pfc-ro-node--action    .pfc-ro-node__header { background: #f5f3ff; border-bottom-color: #e0e7ff; }
.pfc-ro-node--macro-start { border-color: #a855f7; }
.pfc-ro-node--macro-start .pfc-ro-node__header { background: #faf5ff; border-bottom-color: #e9d5ff; }
.pfc-ro-node--macro-end   { border-color: #7c3aed; }
.pfc-ro-node--macro-end   .pfc-ro-node__header { background: #f5f3ff; border-bottom-color: #ddd6fe; }
.pfc-ro-node--macro-call  { border-color: #8b5cf6; }
.pfc-ro-node--macro-call  .pfc-ro-node__header { background: #f5f3ff; border-bottom-color: #e0d7ff; }

/* Macro port colours */
.pfc-ro-handle--port-0 { background: #7c3aed; border-color: #5b21b6; color: #fff; }
.pfc-ro-handle--port-1 { background: #2563eb; border-color: #1d4ed8; color: #fff; }
.pfc-ro-handle--port-2 { background: #059669; border-color: #047857; color: #fff; }
.pfc-ro-handle--port-3 { background: #d97706; border-color: #b45309; color: #fff; }
.pfc-ro-handle--port-4 { background: #dc2626; border-color: #b91c1c; color: #fff; }
.pfc-ro-handle--port-5 { background: #0891b2; border-color: #0e7490; color: #fff; }
</style>
